/**
 * Builds `AdCampaignWizard` initial state from `adCampaignForEdit` (full campaign or single ad set).
 */

import { filterSupportedPlacements } from '@/utils/ad-campaign-constants'
import { EXCLUSION_STAGE, getExclusionChoice } from '@/utils/audience-exclusions'
import { WARM_AUDIENCE_ID, WARM_AUDIENCE_OPTION } from '@/utils/warm-audiences'
import { LOOKALIKE_AUDIENCE_ID, LOOKALIKE_KIND } from '@/utils/lookalike-audiences'
import { ACCOUNT_AUDIENCE_FALLBACK_NAME, accountAudienceWizardId } from '@/utils/account-audiences'
import { ADVANTAGE_LEADS_BUDGET_TYPE, campaignBudgetStepData } from '@/utils/advantage-leads'
import { CAMPAIGN_BUDGET_TYPE, plainCampaignBudgetStepData, typedLimitsOf } from '@/utils/campaign-budget'

/** Shape returned by `adCampaignForEdit` / `GET_AD_CAMPAIGN_FOR_EDIT`. */
export interface AdCampaignForEditAdSet {
    id: string
    name?: string | null
    external_id?: string | null
    audience_id: string
    sales_funnel_stage: string
    budget?: number | null
    settings?: string | null
    ad_creative_ids: string[]
    audience_hypothesis_id?: string | null
    audience_mode?: string | null
    /** Meta id of the ad account audience the ad set targets, when it does. */
    account_audience_id?: string | null
    account_audience_name?: string | null
    account_audience_subtype?: string | null
    /** Daily spend limits (minor units) of an ad set of a campaign with a campaign budget; null for none. */
    min_spend_target?: number | null
    spend_cap?: number | null
}

/** Label and kind of an AI audience, looked up separately because the ad set only stores its id. */
/** A behaviour or demographic an AI audience carries next to its interests. */
export interface HypothesisTargetingOption {
    optionClass: string
    id: string
    name: string
}

export type HypothesisLabels = Record<
    string,
    { label?: string | null; kind?: string | null; targetingOptions?: HypothesisTargetingOption[] }
>

/** An ad account audience of an ad set in the wizard, as `salesFunnelSetup.accountAudiences` keeps it. */
export interface EditAccountAudience {
    id: string
    external_id: string
    name: string
    subtype: string | null
    /** The catalogue audience id of the ad set, by funnel stage. */
    stages: Record<string, string>
}

/** An AI audience of an ad set in the wizard; its interests cannot be edited. */
export interface EditHypothesisAudience {
    id: string
    kind: string | null
    label: string
    catalogueAudienceId: string
    audienceMode: string | null
    interests: { id: string; name: string }[]
    targetingOptions?: HypothesisTargetingOption[]
    readOnly: true
}

export interface AdCampaignForEditData {
    id: string
    name: string
    objective: string
    location_targeting?: string | null
    start_time?: string | null
    ad_sets?: AdCampaignForEditAdSet[]
    /** `advantage_leads` or `campaign_budget` for a campaign with one campaign budget; absent or null otherwise. */
    budget_mode?: string | null
    /** Daily campaign budget (minor units) of such a campaign. */
    daily_budget?: number | null
}

export interface BuildAdCampaignEditWizardInitialStateOptions {
    filterAdSetId?: string | null
    hypothesisLabels?: HypothesisLabels
}

/**
 * Key the wizard uses for an ad set's audience. An ad set built from an AI audience is keyed by its hypothesis,
 * because several of them can share one catalogue audience. An ad set of an ad account audience is keyed like in
 * a new campaign. Other ad sets use their catalogue audience.
 */
function wizardAudienceId(adSet: AdCampaignForEditAdSet): string {
    if (adSet.account_audience_id) return accountAudienceWizardId(adSet.account_audience_id)
    return adSet.audience_hypothesis_id || adSet.audience_id
}

/** Parsed fragment from ad set `settings` JSON (subset used by the wizard). */
interface ParsedFunnelStageSettings {
    gender_targets?: string[]
    age_targets?: string[]
    device_targets?: string[]
    placement_targets?: string[]
    included_countries?: string[]
    excluded_countries?: string[]
    custom_event_type?: string | null
    recency_days?: number | null
    locales?: { key: number; name?: string | null }[]
    exclusions?: Record<string, unknown> | null
}

/** One audience’s targeting row under `audienceTargeting.settings[stage][audienceId]`. */
export interface AudienceTargetingWizardEntry {
    gender_targets: string[]
    age_targets: string[]
    device_targets: string[]
    platform_placement_targets: string[]
    countries: { included: string[]; excluded: string[] }
    custom_event_type: string | null
    recency_days: number | null
    /** Present only when the ad set has a language setting. */
    locales?: { key: number; name: string }[]
}

type StageKey = string
type AudienceKey = string

function parseAdSetSettings(raw: string | null | undefined): ParsedFunnelStageSettings | null {
    if (raw == null || raw === '') return null
    try {
        const parsed: unknown = JSON.parse(raw)
        if (typeof parsed !== 'object' || parsed === null) return null
        return parsed as ParsedFunnelStageSettings
    } catch {
        return null
    }
}

function buildAudienceTargetingSettings(
    adSets: AdCampaignForEditAdSet[],
): Record<StageKey, Record<AudienceKey, AudienceTargetingWizardEntry>> {
    const settings: Record<StageKey, Record<AudienceKey, AudienceTargetingWizardEntry>> = {}

    for (const adSet of adSets) {
        const parsedSettings = parseAdSetSettings(adSet.settings ?? null)
        if (!parsedSettings) continue

        const stage = adSet.sales_funnel_stage
        const audienceId = wizardAudienceId(adSet)
        if (!settings[stage]) settings[stage] = {}

        settings[stage][audienceId] = {
            gender_targets: parsedSettings.gender_targets ?? ['male', 'female'],
            age_targets: parsedSettings.age_targets ?? ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'],
            device_targets: parsedSettings.device_targets ?? ['desktop', 'mobile_ios', 'mobile_android'],
            platform_placement_targets: filterSupportedPlacements(parsedSettings.placement_targets),
            countries: {
                included: parsedSettings.included_countries ?? [],
                excluded: parsedSettings.excluded_countries ?? [],
            },
            custom_event_type: parsedSettings.custom_event_type ?? null,
            recency_days: parsedSettings.recency_days !== undefined ? parsedSettings.recency_days : null,
            ...(Array.isArray(parsedSettings.locales)
                ? {
                      locales: parsedSettings.locales.map((locale) => ({
                          key: locale.key,
                          name: locale.name ?? String(locale.key),
                      })),
                  }
                : {}),
        }
    }

    return settings
}

export interface AdCampaignEditWizardPrefetchedData {
    campaignName: string
    publisherNameStamp: string
    adSetMapping: Record<string, string>
    isEditMode: boolean
    adCampaignId: string
    originalCampaignName: string
    isEditAdSetMode?: boolean
    adSetId?: string
    singleAdSetExternalId?: string | null
    /** `${stage}_${audienceId}` keys of the edited ad sets that are already published on Meta. */
    publishedAdSetKeys?: string[]
    isDuplicateFlow?: boolean
    sourceAdCampaignName?: string
    /** `${stage}_${audienceId}` keys of the edited ad sets that have a language setting. */
    localeAdSetKeys?: string[]
    /** Stored daily budget (minor units) of each edited ad set when the edit was opened, by `${stage}_${audienceId}`. */
    adSetBudgets?: Record<string, number>
    /** Stored daily campaign budget (minor units) when the edit was opened; only with one campaign budget. */
    campaignDailyBudget?: number
    /** Spend limits (minor units) each ad set was opened with, by ad set key; null for none. */
    adSetLimits?: Record<string, { min: number | null; max: number | null }>
}

export interface AdCampaignEditWizardInitialState {
    contentCache: {
        campaignObjective: { selectedObjective: string }
        locationTargeting: { selectedLocation: string | null | undefined }
        salesFunnelSetup: {
            audienceSelections: Record<StageKey, string[]>
            adCreativeSelections: Record<string, string[]>
            hypothesisAudiences?: Record<string, EditHypothesisAudience>
            /** Lookalike ad sets, kept apart from the AI audiences like in a new wizard. */
            lookalikeAudiences?: Record<string, EditHypothesisAudience>
            /** The lead form retargeting option, when an ad set uses it; the general audience list lacks it. */
            warmAudiences?: Array<typeof WARM_AUDIENCE_OPTION>
            /** Ad account audiences of the ad sets, by wizard id; they cannot be changed in edit mode. */
            accountAudiences?: Record<string, EditAccountAudience>
        }
        audienceTargeting: {
            settings: Record<StageKey, Record<AudienceKey, AudienceTargetingWizardEntry>>
            /** Campaign-level exclusion choice; set only when duplicating a campaign that used exclusions. */
            exclusions?: Record<string, boolean>
        }
        budgetSchedule: {
            budgetType: 'manual' | typeof ADVANTAGE_LEADS_BUDGET_TYPE | typeof CAMPAIGN_BUDGET_TYPE
            /** Daily campaign budget as shown in the wizard (major units); only with one campaign budget. */
            campaignBudget?: string
            /** Spend limits as typed (major units), by ad set key; only with a plain campaign budget. */
            adSetLimits?: Record<string, { min: string; max: string }>
            manualBudgets: Record<
                StageKey,
                {
                    audiences: Record<AudienceKey, { amount: string }>
                }
            >
        }
        reviewLaunch: { audienceNames: Record<string, string> }
        prefetchedData: AdCampaignEditWizardPrefetchedData
    }
}

export function buildAdCampaignEditWizardInitialState(
    campaignData: AdCampaignForEditData,
    options: BuildAdCampaignEditWizardInitialStateOptions = {},
): AdCampaignEditWizardInitialState | null {
    const { filterAdSetId = null, hypothesisLabels = {} } = options
    const allAdSets = campaignData.ad_sets ?? []
    const adSets =
        filterAdSetId != null && filterAdSetId !== ''
            ? allAdSets.filter((s) => String(s.id) === String(filterAdSetId))
            : allAdSets

    if (filterAdSetId != null && filterAdSetId !== '' && adSets.length === 0) {
        return null
    }

    const audienceSelections: Record<StageKey, string[]> = {}
    const adCreativeSelections: Record<string, string[]> = {}

    const hypothesisAudiences: Record<string, EditHypothesisAudience> = {}
    const lookalikeAudiences: Record<string, EditHypothesisAudience> = {}
    const accountAudiences: Record<string, EditAccountAudience> = {}

    for (const adSet of adSets) {
        const stage = adSet.sales_funnel_stage
        const audienceId = wizardAudienceId(adSet)
        if (!audienceSelections[stage]) audienceSelections[stage] = []
        if (!audienceSelections[stage].includes(audienceId)) {
            audienceSelections[stage].push(audienceId)
        }
        if (adSet.ad_creative_ids.length > 0) {
            const key = `${stage}_${audienceId}`
            adCreativeSelections[key] = adSet.ad_creative_ids
        }
        if (adSet.account_audience_id) {
            const entry = accountAudiences[audienceId] ?? {
                id: audienceId,
                external_id: adSet.account_audience_id,
                name: String(adSet.account_audience_name ?? '').trim() || ACCOUNT_AUDIENCE_FALLBACK_NAME,
                subtype: adSet.account_audience_subtype ?? null,
                stages: {},
            }
            entry.stages[stage] = adSet.audience_id
            accountAudiences[audienceId] = entry
        }
        if (adSet.audience_hypothesis_id) {
            const stored = hypothesisLabels[adSet.audience_hypothesis_id]
            const isLookalike = adSet.audience_id === LOOKALIKE_AUDIENCE_ID
            const target = isLookalike ? lookalikeAudiences : hypothesisAudiences
            target[adSet.audience_hypothesis_id] = {
                id: adSet.audience_hypothesis_id,
                kind: isLookalike ? LOOKALIKE_KIND : (stored?.kind ?? null),
                label: stored?.label || (adSet.name != null ? String(adSet.name).trim() : '') || 'AI audience',
                catalogueAudienceId: adSet.audience_id,
                audienceMode: adSet.audience_mode ?? null,
                interests: [],
                ...(stored?.targetingOptions?.length ? { targetingOptions: stored.targetingOptions } : {}),
                readOnly: true,
            }
        }
    }

    const manualBudgets: AdCampaignEditWizardInitialState['contentCache']['budgetSchedule']['manualBudgets'] = {}

    for (const adSet of adSets) {
        const stage = adSet.sales_funnel_stage
        const audienceId = wizardAudienceId(adSet)
        if (!manualBudgets[stage]) manualBudgets[stage] = { audiences: {} }
        manualBudgets[stage].audiences[audienceId] = {
            amount: adSet.budget != null ? String(adSet.budget / 100) : '0',
        }
    }

    const adSetMapping: Record<string, string> = {}
    const audienceNames: Record<string, string> = {}
    const publishedAdSetKeys: string[] = []
    const localeAdSetKeys: string[] = []
    const adSetBudgets: Record<string, number> = {}
    const openedLimits: Record<string, { min: number | null; max: number | null }> = {}
    const typedLimits: Record<string, { min: string; max: string }> = {}

    for (const adSet of adSets) {
        const key = `${adSet.sales_funnel_stage}_${wizardAudienceId(adSet)}`
        adSetMapping[key] = adSet.id
        if (adSet.budget != null) adSetBudgets[key] = adSet.budget
        openedLimits[key] = { min: adSet.min_spend_target ?? null, max: adSet.spend_cap ?? null }
        typedLimits[key] = typedLimitsOf(adSet.min_spend_target, adSet.spend_cap)
        if (adSet.external_id) publishedAdSetKeys.push(key)
        if (Array.isArray(parseAdSetSettings(adSet.settings ?? null)?.locales)) localeAdSetKeys.push(key)
        const trimmed = adSet.name != null ? String(adSet.name).trim() : ''
        if (trimmed) audienceNames[key] = trimmed
    }

    const prefetchedData: AdCampaignEditWizardPrefetchedData = {
        campaignName: campaignData.name,
        publisherNameStamp: 'Turba Media',
        adSetMapping,
        isEditMode: true,
        adCampaignId: campaignData.id,
        originalCampaignName: campaignData.name,
        publishedAdSetKeys,
        ...(localeAdSetKeys.length > 0 ? { localeAdSetKeys } : {}),
        adSetBudgets,
        ...((campaignData.budget_mode === ADVANTAGE_LEADS_BUDGET_TYPE ||
            campaignData.budget_mode === CAMPAIGN_BUDGET_TYPE) &&
        campaignData.daily_budget != null
            ? { campaignDailyBudget: Number(campaignData.daily_budget) }
            : {}),
        ...(campaignData.budget_mode === CAMPAIGN_BUDGET_TYPE ? { adSetLimits: openedLimits } : {}),
    }

    if (filterAdSetId != null && filterAdSetId !== '') {
        prefetchedData.isEditAdSetMode = true
        prefetchedData.adSetId = filterAdSetId
        prefetchedData.singleAdSetExternalId = adSets[0]?.external_id ?? null
    }

    const usesWarmAudience = adSets.some((adSet) => adSet.audience_id === WARM_AUDIENCE_ID)

    return {
        contentCache: {
            campaignObjective: { selectedObjective: campaignData.objective },
            locationTargeting: { selectedLocation: campaignData.location_targeting },
            salesFunnelSetup: {
                audienceSelections,
                adCreativeSelections,
                ...(Object.keys(hypothesisAudiences).length > 0 ? { hypothesisAudiences } : {}),
                ...(Object.keys(lookalikeAudiences).length > 0 ? { lookalikeAudiences } : {}),
                ...(Object.keys(accountAudiences).length > 0 ? { accountAudiences } : {}),
                ...(usesWarmAudience ? { warmAudiences: [WARM_AUDIENCE_OPTION] } : {}),
            },
            audienceTargeting: { settings: buildAudienceTargetingSettings(adSets) },
            budgetSchedule:
                campaignData.budget_mode === ADVANTAGE_LEADS_BUDGET_TYPE
                    ? {
                          budgetType: ADVANTAGE_LEADS_BUDGET_TYPE,
                          campaignBudget: campaignBudgetStepData(campaignData.daily_budget).campaignBudget,
                          manualBudgets,
                      }
                    : campaignData.budget_mode === CAMPAIGN_BUDGET_TYPE
                      ? {
                            ...plainCampaignBudgetStepData(campaignData.daily_budget, typedLimits),
                            budgetType: CAMPAIGN_BUDGET_TYPE as 'campaign_budget',
                            manualBudgets,
                        }
                      : { budgetType: 'manual', manualBudgets },
            reviewLaunch: {
                audienceNames,
            },
            prefetchedData,
        },
    }
}

/**
 * Exclusion choice of a campaign, from the stored settings of its acquisition ad sets: a kind is chosen when
 * any of them asks for it.
 */
function exclusionChoiceOf(adSets: AdCampaignForEditAdSet[]): Record<string, boolean> {
    const choice: Record<string, boolean> = {}
    for (const adSet of adSets) {
        if (adSet.sales_funnel_stage !== EXCLUSION_STAGE) continue
        Object.assign(choice, getExclusionChoice(parseAdSetSettings(adSet.settings ?? null)))
    }
    return choice
}

/**
 * Prefills the full create wizard from an existing ad campaign; submit uses `createAdCampaign`. The source's
 * exclusion choice is kept; the wizard and the server check again whether it is available.
 */
export function buildAdCampaignDuplicateWizardInitialState(
    campaignData: AdCampaignForEditData,
    options: Pick<BuildAdCampaignEditWizardInitialStateOptions, 'hypothesisLabels'> = {},
): AdCampaignEditWizardInitialState | null {
    const base = buildAdCampaignEditWizardInitialState(campaignData, options)
    if (!base) return null

    const sourceName = campaignData.name
    // A duplicate creates new ad sets, so no ad set has a stored language setting or budget yet
    const {
        localeAdSetKeys: _editedLocaleKeys,
        adSetBudgets: _editedBudgets,
        campaignDailyBudget: _editedCampaignBudget,
        adSetLimits: _editedLimits,
        ...prefetchedData
    } = base.contentCache.prefetchedData
    const exclusions = exclusionChoiceOf(campaignData.ad_sets ?? [])
    return {
        ...base,
        contentCache: {
            ...base.contentCache,
            ...(Object.keys(exclusions).length > 0
                ? { audienceTargeting: { ...base.contentCache.audienceTargeting, exclusions } }
                : {}),
            prefetchedData: {
                ...prefetchedData,
                adSetMapping: {},
                publishedAdSetKeys: [],
                isEditMode: false,
                adCampaignId: '',
                isDuplicateFlow: true,
                sourceAdCampaignName: sourceName,
            },
        },
    }
}

/**
 * True when the wizard edits an ad set that is already published on Meta. Settings that only take
 * effect when an audience is created (event type, recency) cannot change such an ad set.
 */
export function isPublishedAdSetInEdit(
    prefetchedData: Partial<AdCampaignEditWizardPrefetchedData> | null | undefined,
    stage: string,
    audienceId: string,
): boolean {
    if (!prefetchedData || (prefetchedData.isEditMode !== true && prefetchedData.isEditAdSetMode !== true)) return false
    return (prefetchedData.publishedAdSetKeys ?? []).includes(`${stage}_${audienceId}`)
}
